/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { useScheduleSettings } from '@sportninja/common/hooks/useScheduleSettings'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { getStatTypesBySportId } from '@sportninja/common/selectors/types'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import {
  Alert,
  Avatar,
  Button,
  Collapse,
  Empty,
  FloatButton,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import { ArrowUpOutlined } from '@ant-design/icons'
import { connect } from 'react-redux'
import ReprocessingSettingsOverlay from 'src/pages/Schedule/ReprocessingSettingsOverlay'
import generateStandingColumns from 'src/utils/generateStandingColumns'
import mapStandingsRow from 'src/utils/mapStandingsRow'

import Icon from '../../Icon'
import GameListFilters from '../../List/GameList/GameListFilters'
import useGetScheduleFiltering from '../../List/useGetScheduleFiltering'
import TieBreakingProcedureModal from './TieBreakingProcedureModal'
import useWindowSize from 'src/hooks/useWindowSize'
import Text from 'src/components/ts-components/Text'

// Legacy column definitions - kept for fallback when settings are not available
const hockeyColumns = [
  {
    key: 'GP',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'GP',
    dataIndex: 'GP',
    showSorterTooltip: {
      title: 'Games Played',
    },
  },
  {
    key: 'W',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'W',
    dataIndex: 'W',
    showSorterTooltip: {
      title: 'Wins',
    },
  },
  {
    key: 'L',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'L',
    dataIndex: 'L',
    showSorterTooltip: {
      title: 'Losses',
    },
  },
  {
    key: 'T',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'T',
    dataIndex: 'T',
    showSorterTooltip: {
      title: 'Ties',
    },
  },
  {
    key: 'OTL',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'OTL',
    dataIndex: 'OTL',
    showSorterTooltip: {
      title: 'Overtime Losses',
    },
  },
  {
    key: 'OTW',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'OTW',
    dataIndex: 'OTW',
    showSorterTooltip: {
      title: 'Overtime Wins',
    },
  },
  {
    key: 'SOL',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'SOL',
    dataIndex: 'SOL',
    showSorterTooltip: {
      title: 'Shootout Losses',
    },
  },
  {
    key: 'PTS',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'PTS',
    dataIndex: 'PTS',
    showSorterTooltip: {
      title: 'Points',
    },
  },
  {
    key: 'WPCT',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'WPCT',
    dataIndex: 'WPCT',
    width: 120,
    showSorterTooltip: {
      title: 'Winning Percentage',
    },
  },
  {
    key: 'WPCTD',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'WPCTD',
    dataIndex: 'WPCTD',
    width: 120,
    defaultSortOrder: 'descend',
    showSorterTooltip: {
      title: 'In-Division Winning Percentage',
    },
  },
  {
    key: 'IN-DIV',
    title: 'DIV',
    dataIndex: 'IN-DIV',
    width: 150,
    showSorterTooltip: {
      // Custom title so we can break it up into multiple lines
      // title: 'In-Division Record (W-L-T-OTL-SOL)',
      title: () => {
        return (
          <span>
            In-Division Record <br />
            (W-L-T-OTL-SOL)
          </span>
        )
      },
    },
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
  },
  {
    key: 'GF',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'GF',
    dataIndex: 'GF',
    showSorterTooltip: {
      title: 'Goals For',
    },
  },
  {
    key: 'GA',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'GA',
    dataIndex: 'GA',
    showSorterTooltip: {
      title: 'Goals Against',
    },
  },
  {
    key: 'GPCT',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'GPCT',
    dataIndex: 'GPCT',
    showSorterTooltip: {
      title: 'Game Percentage',
    },
  },
  {
    key: 'PIM',
    sorter: (a, b) => 0,
    sortDirections: ['ascend', 'descend', 'ascend'],
    title: 'PIM',
    dataIndex: 'PIM',
    showSorterTooltip: {
      title: 'Penalty Minutes',
    },
  },
]

const Standings = ({
  areFiltersHidden,
  setIsFilterToggleDisabled = () => {},
  id,
  defaultSort,
  refreshKey,
  scheduleReprocessingListener,
  statWhitelist = [],
  shouldStickyHeader = true,
  hideGroupName = false,
  // Legacy props - kept for backward compatibility but no longer used
  isSoccer = false,
  isInFrame = false,
  isGenericSport = false,
  sportId,
  statTypes,
  isAdmin = false,
  setOutsideDismissed = () => {},
  dismissed = false,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const { scheduleSettings, fetchScheduleSettings } = useScheduleSettings()

  const settingsAppliedRef = useRef(false)

  const { standings } = scheduleSettings?.settings ?? {}
  const { sort_order, displayed } = standings ?? {}
  const { stat_id, tie_breaker_stats } = sort_order ?? {}
  const defaultStatType = statTypes?.find((s) => s.id === stat_id)

  // Generate dynamic columns based on settings
  const dynamicColumns = useMemo(() => {
    if (!displayed?.length || !statTypes?.length) {
      // Fallback to hockey columns if no settings available
      return hockeyColumns
    }

    return generateStandingColumns(
      displayed,
      statTypes,
      {
        primaryId: stat_id,
        tieBreakerIds: tie_breaker_stats,
      },
      { disableLocalSorting: true }
    )
  }, [displayed, statTypes, stat_id, tie_breaker_stats])

  const [isTieBreakingModalVisible, setIsTieBreakingModalVisible] =
    useState(false)

  /**
   * Memoized common columns configuration for the standings table
   * This prevents unnecessary re-renders when other component state changes
   */
  const commonColumns = useMemo(
    () => [
      {
        title: 'TEAM',
        dataIndex: 'team',
        render: (item) => {
          return (
            <Link
              to={`/team/${item?.team?.id}?sn_schedule=${id}`}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <Avatar
                src={
                  getImageThumbnailId(item?.team) ||
                  '/images/placeholders/hockey/team.png'
                }
                shape='square'
                icon={<Icon name='user-friends' fontSize={14} />}
                css={css`
                  border: 1px solid #dbdbdb;
                  background-color: white;
                  padding: 2px;
                `}
              />
              <Typography.Text strong>{item?.team?.name_full}</Typography.Text>
            </Link>
          )
        },
        key: 'team',
        width: 300,
      },
    ],
    [id]
  ) // Only re-compute when id changes

  const { filters, state, setState, loaded, currentParams } =
    useGetScheduleFiltering({ schedule_id: id }, { defaultSort: 'desc' })
  const [scheduleId, setScheduleId] = useState(currentParams?.sn_division ?? id)
  const [grouping, setGrouping] = useState(filters?.[filters?.length - 1]?.name)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [groups, setGroups] = useState([])
  const groupList = useMemo(() => Object.keys(groups), [groups])
  const activeListItems = useMemo(
    () => groupList.map((_, i) => i.toString()),
    [groupList]
  )
  const [expandAllItems, setExpandAllItems] = useState(true)
  const [activeKey, setActiveKey] = useState(null)
  const [error, setError] = useState(null)
  const [sortBy, setSortBy] = useState(null)
  const [direction, setDirection] = useState('desc')
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  // Sync sortBy and direction with stat_id when settings are loaded (only once)
  useEffect(() => {
    if (stat_id && statTypes?.length && !settingsAppliedRef.current) {
      setSortBy(stat_id)
      // Set initial direction based on is_sort_desc property
      const statType = statTypes.find((s) => s.id === stat_id)
      if (statType) {
        setDirection(statType.is_sort_desc ? 'desc' : 'asc')
      }
      settingsAppliedRef.current = true
    }
  }, [stat_id, statTypes])

  // Create mapping from abbreviation to stat_id for table onChange handler
  const statIdByAbbr = useMemo(() => {
    if (!statTypes || !Array.isArray(statTypes)) {
      return {}
    }
    return statTypes.reduce((acc, statType) => {
      if (statType.abbreviation && statType.id) {
        acc[statType.abbreviation] = statType.id
      }
      return acc
    }, {})
  }, [statTypes])

  // Convert sortBy (stat_id) to abbreviation for column matching
  const currentSortAbbr = useMemo(() => {
    if (!sortBy || !statTypes?.length) {
      return null
    }
    // Try to find by ID first (use String() for type safety)
    const statById = statTypes.find((s) => String(s.id) === String(sortBy))
    if (statById) {
      return statById.abbreviation
    }
    // Fallback: sortBy might already be an abbreviation (e.g., from legacy URL)
    const statByAbbr = statTypes.find((s) => s.abbreviation === sortBy)
    if (statByAbbr) {
      return statByAbbr.abbreviation
    }
    // No match found - return null to avoid false matches
    return null
  }, [sortBy, statTypes])

  // Apply dynamic sortOrder to columns based on current sortBy state
  const columnsWithSortOrder = useMemo(() => {
    if (!currentSortAbbr) {
      return dynamicColumns
    }

    return dynamicColumns.map((col) => {
      if (col.key === currentSortAbbr) {
        return {
          ...col,
          sortOrder: direction === 'desc' ? 'descend' : 'ascend',
        }
      }
      // Remove defaultSortOrder from non-active columns to prevent conflict
      const { defaultSortOrder, ...rest } = col
      return rest
    })
  }, [dynamicColumns, currentSortAbbr, direction])

  const shouldDisplayTieBreakingModal = useMemo(() => {
    if (!isCanlan) {
      return true
    }
    // Only show if there the user has admin permissions
    return isAdmin
  }, [isAdmin])

  useEffect(() => {
    if (id && !scheduleSettings) {
      fetchScheduleSettings(id)
    }
  }, [id, scheduleSettings, fetchScheduleSettings])

  // Reset settingsAppliedRef when schedule id changes
  useEffect(() => {
    settingsAppliedRef.current = false
  }, [id])

  useEffect(() => {
    return () => {
      setIsFilterToggleDisabled(false)
    }
  }, [])

  useEffect(() => {
    // Wait for schedule filters to finish loading, then set the grouping and load statistics
    if (loaded) {
      setGrouping(filters?.[filters?.length - 1]?.name || '')
      setReady(true)
      if (filters?.length === 0) {
        setIsFilterToggleDisabled(true)
      }
    }
  }, [loaded, filters?.length])

  const getItems = async (page) => {
    // Guard: don't fetch until settings are applied and sortBy is set
    if (!settingsAppliedRef.current || !sortBy) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      let groupBy
      if (grouping) {
        groupBy = grouping
      }

      // const slug = `/schedules/${scheduleId}/stats/byType`
      const slug = `/schedules/${scheduleId}/standings`

      const response = await req(slug, {
        query: { groupBy, page, sortBy, sort: direction },
      })

      const g = {}
      response.data.forEach((row) => {
        g[row.schedule.id] = g[row.schedule.id] || {}
        g[row.schedule.id].rows = g[row.schedule.id].rows || []
        g[row.schedule.id].name = row.schedule.name
        if (row?.team !== null) {
          g[row.schedule.id].rows.push(row)
        }
      })

      setItems(response.data)
      setGroups(g)
      if (!hasLoadedOnce) {
        setHasLoadedOnce(true)
      }
    } catch (e) {
      console.error('🚨 getItems failed:', e)
      setError(getErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    ready && getItems()
  }, [ready, scheduleId, grouping, refreshKey, sortBy, direction])

  const collapseItems = useMemo(
    () => [
      ...groupList.map((groupId, index) => {
        const group = groups[groupId]
        return {
          key: String(index),
          label: group?.name,
          children: (
            <Table
              columns={commonColumns.concat(columnsWithSortOrder)}
              bordered
              loading={loading}
              size='small'
              scroll={{ x: isMobile || width < 1200 ? 1500 : undefined }}
              pagination={false}
              dataSource={group.rows.map(mapStandingsRow)}
              onChange={(paginationInfo, tableFilters, sorter) => {
                const columnKeyMap = {
                  player: 'player.full_name',
                  team: 'team.name',
                }
                const key = sorter?.columnKey
                const mappedKey = columnKeyMap[key] || statIdByAbbr[key] || key
                setSortBy(mappedKey)

                setDirection(sorter?.order === 'descend' ? 'desc' : 'asc')
              }}
              css={css`
                .ant-table-cell {
                  color: ${colors.NEUTRAL_0} !important;
                }
                .ant-table-measure-row {
                  display: none !important;
                }
                .ant-table-thead > tr > th {
                  padding: 8px 8px !important;
                  background-color: ${colors.SECONDARY_300} !important;
                }
                .ant-table-thead > tr > th.ant-table-column-sort {
                  isolation: isolate;
                  background-color: transparent !important;
                  position: relative;
                }
                .ant-table-thead > tr > th.ant-table-column-sort::after {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background-color: ${colors.SECONDARY_300};
                  background-image: linear-gradient(
                    ${colors.INFO_500},
                    ${colors.INFO_500}
                  );
                  background-blend-mode: color-dodge;
                  z-index: -1;
                  pointer-events: none;
                }
                .ant-table-tbody > tr:nth-child(even) {
                  background-color: ${colors.SECONDARY_400};
                }
                .ant-table-tbody > tr:nth-child(odd) {
                  background-color: ${colors.SECONDARY_300};
                }
                .ant-table-tbody > tr > td {
                  padding: 8px 8px !important;
                  vertical-align: middle;
                }
                .ant-table-tbody > tr > td.ant-table-column-sort {
                  isolation: isolate;
                  background-color: transparent !important;
                }
                .ant-table-tbody
                  > tr
                  > td.ant-table-column-sort:not(.ant-table-cell-fix-left):not(
                    .ant-table-cell-fix-right
                  ) {
                  position: relative;
                }
                .ant-table-tbody > tr > td.ant-table-column-sort::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background-image: linear-gradient(
                    ${colors.INFO_500},
                    ${colors.INFO_500}
                  );
                  background-blend-mode: color-dodge;
                  z-index: -1;
                  pointer-events: none;
                }
                .ant-table-tbody
                  > tr:nth-child(even)
                  > td.ant-table-column-sort::before {
                  background-color: ${colors.SECONDARY_400};
                }
                .ant-table-tbody
                  > tr:nth-child(odd)
                  > td.ant-table-column-sort::before {
                  background-color: ${colors.SECONDARY_300};
                }
                .ant-table-tbody
                  > tr
                  > td.ant-table-column-sort.ant-table-cell-fix-left {
                  background-color: transparent !important;
                }
                .ant-table-tbody
                  > tr:nth-child(even)
                  > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
                  background-color: ${colors.SECONDARY_400} !important;
                }
                .ant-table-tbody
                  > tr:nth-child(odd)
                  > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
                  background-color: ${colors.SECONDARY_300} !important;
                }
                .ant-table-tbody > tr:nth-child(even) > td:nth-child(1) {
                  background-color: ${colors.SECONDARY_400} !important;
                }
                .ant-table-tbody > tr:nth-child(odd) > td:nth-child(1) {
                  background-color: ${colors.SECONDARY_300} !important;
                }
              `}
            />
          ),
        }
      }),
    ],
    [
      commonColumns,
      columnsWithSortOrder,
      groupList,
      groups,
      loading,
      statIdByAbbr,
    ]
  )

  return (
    <>
      {/* <ReprocessingOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
      /> */}
      <ReprocessingSettingsOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
        setOutsideDismissed={setOutsideDismissed}
      />
      <div
        css={css`
          margin-top: 24px;
          ${scheduleReprocessingListener &&
          !dismissed &&
          css`
            opacity: 0.7;
            filter: blur(2px);
            pointer-events: none;
          `}
        `}
      >
        <GameListFilters
          displayLabels={false}
          filters={filters}
          state={state}
          setState={setState}
          loading={loading}
          compactFilters
          childrenInFirstRow
          onScheduleChange={(newScheduleId, scheduleName) => {
            if (newScheduleId) {
              setScheduleId(newScheduleId)
              setGrouping(scheduleName)
            } else {
              setScheduleId(id)
              setGrouping(filters?.[filters?.length - 1]?.name || '')
            }
          }}
          isHidden={areFiltersHidden}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-direction: row;
              gap: 16px;
            `}
          >
            <Button
              type='default'
              onClick={() => {
                if (expandAllItems) {
                  setExpandAllItems(false)
                  setActiveKey(null)
                } else {
                  setExpandAllItems(true)
                  setActiveKey(activeListItems)
                }
              }}
            >
              <Text
                variant='paragraphXSmall'
                color={colors.NEUTRAL_0}
                weight='regular'
              >
                {expandAllItems ? 'Collapse All' : 'Expand All'}
              </Text>
            </Button>
            {shouldDisplayTieBreakingModal ? (
              <Tooltip title='Tie-Breaking Procedure'>
                <Button
                  css={
                    !isInFrame
                      ? css``
                      : css`
                          border: 1px solid rgba(0, 0, 0, 0.3);
                          color: ${colors.HEADER};
                        `
                  }
                  type='default'
                  icon={<Icon name='info-circle' />}
                  onClick={() => setIsTieBreakingModalVisible(true)}
                />
              </Tooltip>
            ) : null}
          </div>
        </GameListFilters>
        {error && (
          <Alert
            description={error}
            type='error'
            showIcon
            css={css`
              margin-bottom: 24px;
            `}
          />
        )}
        {hasLoadedOnce && items?.length === 0 && !loading ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            css={css`
              margin-top: 24px;
              opacity: 1;
              transition: opacity 1s ease-in-out;
            `}
          />
        ) : null}
        {!hasLoadedOnce && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 400px;
              opacity: 1;
              transition: opacity 1s ease-in-out;
            `}
          >
            <Spin />
          </div>
        )}
        {hasLoadedOnce && groupList.length > 0 && (
          <Collapse
            css={css`
              opacity: ${loading ? 0.7 : 1};
              transition: opacity 0.2s ease-in-out;

              .ant-collapse-content > .ant-collapse-content-box {
                padding: 0px !important;
              }
              ${isInFrame &&
              css`
                .ant-collapse-header-text {
                  color: ${colors.HEADER};
                }
                .ant-collapse-expand-icon {
                  color: ${colors.HEADER};
                }
              `}
            `}
            items={collapseItems}
            activeKey={expandAllItems ? activeListItems : activeKey}
            onChange={(e) => {
              setExpandAllItems(false)
              setActiveKey(e)
            }}
            style={{ marginBottom: 24 }}
          />
        )}
        <TieBreakingProcedureModal
          isOpen={isTieBreakingModalVisible}
          onClose={() => setIsTieBreakingModalVisible(false)}
          tieBreakingStats={tie_breaker_stats}
          statTypes={statTypes}
          defaultSortBy={stat_id}
        />
      </div>
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${PRIMARY_BUTTON_CONTENT.icon} !important;
          }
        `}
      />
    </>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    statTypes: getStatTypesBySportId(state, sportId),
  }
}

const mapDispatchToProps = (_) => {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(Standings)
